if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initControls();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Counter and arrows under the image. On mobile the images are a swipe
      // slider, so the position follows its scroll; from 750px up only the
      // active image shows (thumbnail layout), so the arrows switch it the
      // way a thumbnail click does.
      initControls() {
        this.controls = this.querySelector('.product__media-controls');
        if (!this.controls) return;

        this.counterCurrent = this.controls.querySelector('.product__media-counter-current');
        this.counterTotal = this.controls.querySelector('.product__media-counter-total');

        this.controls.querySelectorAll('[data-gallery-step]').forEach((button) => {
          button.addEventListener('click', () => this.step(Number(button.dataset.galleryStep)));
        });

        this.slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        this.slider?.addEventListener('scroll', debounce(this.syncWithScroll.bind(this), 100));

        // Switching to the swipe slider, start it at the image that was active.
        this.mql.addEventListener('change', () => {
          const active = this.slider?.querySelector('.is-active');
          if (this.isSwipeSlider && active) this.slider.scrollTo({ left: active.offsetLeft });
          this.updateCounter();
        });

        // The image is narrower than the gallery and centred in it, so keep
        // the controls in line with its edges rather than the gallery's.
        new ResizeObserver(() => this.alignControls()).observe(this.elements.viewer);

        this.updateCounter();
      }

      // Measures the image box: height-capped, it can be narrower than its slide.
      alignControls() {
        const slide = this.slides[this.currentIndex()];
        const media = slide?.querySelector('.product-media-container') || slide;
        if (!media?.offsetWidth) return;
        const inset = Math.max((this.elements.viewer.clientWidth - media.offsetWidth) / 2, 0);
        this.controls.style.setProperty('--media-controls-inset', `${inset}px`);
      }

      get slides() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      get isSwipeSlider() {
        return !this.mql.matches;
      }

      currentIndex() {
        const slides = this.slides;
        if (this.isSwipeSlider && this.slider) {
          const scrollLeft = this.slider.scrollLeft;
          let closest = 0;
          slides.forEach((slide, index) => {
            if (Math.abs(slide.offsetLeft - scrollLeft) < Math.abs(slides[closest].offsetLeft - scrollLeft)) closest = index;
          });
          return closest;
        }
        return Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      }

      // Wraps around at either end.
      step(direction) {
        const slides = this.slides;
        const target = slides[(this.currentIndex() + direction + slides.length) % slides.length];

        if (this.isSwipeSlider && this.slider) {
          this.slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        } else {
          this.setActiveMedia(target.dataset.mediaId, false);
        }
      }

      // A swipe doesn't change the active image, so carry it over: otherwise
      // widening the window past 750px would show a different one.
      syncWithScroll() {
        if (!this.isSwipeSlider) return;
        const slides = this.slides;
        const current = slides[this.currentIndex()];
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === current));
        this.updateCounter();
      }

      updateCounter() {
        if (!this.controls) return;
        this.counterCurrent.textContent = this.currentIndex() + 1;
        this.counterTotal.textContent = this.slides.length;
        this.alignControls();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!this.isSwipeSlider) this.updateCounter();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // The thumbnail column is vertical from 750px up
        if (this.elements.thumbnails.isVertical && this.mql.matches) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
